import moment from 'moment';

import { Injectable } from '@angular/core';
import { Subject }    from 'rxjs';

import { ApiService }     from '../api/api.service';
import { CompanyService } from '../companies/company.service';
import { IntelligenceCustomerFiltersModel } from './typings/intelligence.customer-filters';
import { IntelligenceCustomersModel }       from './typings/intelligence.customers';
import { IntelligenceDealershipStatsModel } from './typings/intelligence.dealership-stats';
import { IntelligenceFinancialsModel }      from './typings/intelligence.financials';
import { IntelligenceInspectionStatsModel } from './typings/intelligence.inspection-stats';
import { IntelligenceListPageModel }        from './typings/intelligence.list-page';
import { IntelligenceMechanicJobModel }     from './typings/intelligence.mechanic-job';
import { IntelligenceMechanicsModel }       from './typings/intelligence.mechanics';
import { IntelligenceOpenSupplierInvoiceModel } from './typings/intelligence.purchasing';
import { IntelligenceOperationsModel }      from './typings/intelligence.operations';
import { IntelligenceOpportunitiesModel }   from './typings/intelligence.opportunities';
import { IntelligenceProductFiltersModel }  from './typings/intelligence.product-filters';
import { IntelligenceProductsModel }        from './typings/intelligence.products';
import { IntelligencePurchasingFiltersModel } from './typings/intelligence.purchasing-filters';
import { IntelligencePurchasingModel }        from './typings/intelligence.purchasing';
import { IntelligenceUnallocatedPurchaseModel } from './typings/intelligence.purchasing';
import { IntelligenceRangeModel }           from './typings/intelligence.range';
import { IntelligenceSummaryModel }         from './typings/intelligence.summary';
import { IntelligenceVehicleFiltersModel }  from './typings/intelligence.vehicle-filters';
import { IntelligenceVehiclesModel }        from './typings/intelligence.vehicles';
import { TableSortModel }                   from './typings/intelligence.table-sort';

@Injectable({
  providedIn: 'root'
})
export class IntelligenceService {
  // The first day the figures go back to. The endpoints pull a range that starts
  // earlier up to this date - only the stored daily figures stop here, so a wider
  // range would pair empty charts with tables that answer anyway - and report it so
  // the dashboard can say why the dates moved. Empty until a tab has loaded.
  public earliestDate  : string = '';
  public previousRange : IntelligenceRangeModel = null;
  public range         : IntelligenceRangeModel = this.presetRange('LAST_30');

  public filtersChanged = new Subject<string>();
  public rangeChanged = new Subject<IntelligenceRangeModel>();

  // What each tab last asked for, so anything that needs to describe the current view
  // can reproduce it rather than fetching the unfiltered data.
  private activeFilters : { [ dataset : string ] : any } = {};

  constructor (
    private apiService    : ApiService,
    private companyService: CompanyService
  ) {}

  // Emails the given tab's report for the current range and filters. The backend
  // builds the same CSV the tab's download produces and sends it as an attachment.
  emailReport ( tab : string, recipient : string ) : Promise<any> {
    return this.apiService.post('/intelligence/email_report', {
      end_date  : this.range.end_date,
      filters   : this.filtersFor(tab) || {},
      recipient,
      start_date: this.range.start_date,
      tab
    });
  }

  // Undefined rather than null when a tab has not loaded yet, so callers passing this
  // straight back into a getter still pick up that getter's default filters.
  filtersFor ( dataset : string ) : any {
    return this.activeFilters[ dataset ];
  }

  getCustomers ( filters : IntelligenceCustomerFiltersModel = { customer: '', customer_type: '', job_type: '', source: '', suburb: '' } ) : Promise<IntelligenceCustomersModel> {
    this.recordFilters('customers', filters);

    return this.withHistoryBoundary(this.apiService.get(`/intelligence/customers?${ this.rangeQuery() }${ this.filtersQuery(filters) }`));
  }

  getFinancials () : Promise<IntelligenceFinancialsModel> {
    return this.withHistoryBoundary(this.apiService.get(`/intelligence/financials?${ this.rangeQuery() }`));
  }

  getDealershipStats () : Promise<IntelligenceDealershipStatsModel> {
    return this.apiService.get('/dashboard/dealership_widget_stats');
  }

  getInspectionStats () : Promise<IntelligenceInspectionStatsModel> {
    return this.apiService.get('/dashboard/inspection_widget_stats');
  }

  getMechanicJobs ( mechanicId : string ) : Promise<{ jobs : Array<IntelligenceMechanicJobModel> }> {
    return this.withHistoryBoundary(this.apiService.get(`/intelligence/mechanics/${ mechanicId }/jobs?${ this.rangeQuery() }`));
  }

  getMechanics ( mechanic = '' ) : Promise<IntelligenceMechanicsModel> {
    this.recordFilters('mechanics', { mechanic });

    return this.withHistoryBoundary(this.apiService.get(`/intelligence/mechanics?${ this.rangeQuery() }${ this.filtersQuery({ mechanic }) }`));
  }

  getOperations () : Promise<IntelligenceOperationsModel> {
    return this.withHistoryBoundary(this.apiService.get(`/intelligence/operations?${ this.rangeQuery() }`));
  }

  getOpportunities () : Promise<IntelligenceOpportunitiesModel> {
    return this.withHistoryBoundary(this.apiService.get(`/intelligence/opportunities?${ this.rangeQuery() }`));
  }

  // One page of one Opportunities list, so a card can page or re-sort without
  // reloading the tab. The range rides along because the quote lists honour it.
  getOpportunitiesList ( list : string, page : number, sort : TableSortModel = null ) : Promise<IntelligenceListPageModel<any>> {
    return this.apiService.get(`/intelligence/opportunities/list?${ this.rangeQuery() }${ this.filtersQuery({ ...this.sortQuery(sort), list, page: '' + page }) }`);
  }

  getProducts ( filters : IntelligenceProductFiltersModel = { brand: '', product_group: '', product_type: '', supplier: '' } ) : Promise<IntelligenceProductsModel> {
    this.recordFilters('products', filters);

    return this.withHistoryBoundary(this.apiService.get(`/intelligence/products?${ this.rangeQuery() }${ this.filtersQuery(filters) }`));
  }

  getPurchasing ( filters : IntelligencePurchasingFiltersModel = { brand: '', product_group: '', supplier: '' } ) : Promise<IntelligencePurchasingModel> {
    this.recordFilters('purchasing', filters);

    return this.withHistoryBoundary(this.apiService.get(`/intelligence/purchasing?${ this.rangeQuery() }${ this.filtersQuery(filters) }`));
  }

  // The chase lists page through dedicated endpoints so a page turn never recomputes
  // the rest of the tab. They are as-of-now lists, so no date range rides along.
  getPurchasingOpenInvoices ( filters : IntelligencePurchasingFiltersModel, page : number, sort : TableSortModel = null, limit : number = 0 ) : Promise<IntelligenceListPageModel<IntelligenceOpenSupplierInvoiceModel>> {
    return this.apiService.get(`/intelligence/purchasing/open_invoices?page=${ page }${ this.filtersQuery({ ...filters, ...this.sortQuery(sort), limit: limit ? '' + limit : '' }) }`);
  }

  getPurchasingPurchaseLines ( filters : IntelligencePurchasingFiltersModel, view : string, page : number, sort : TableSortModel = null, limit : number = 0 ) : Promise<IntelligenceListPageModel<IntelligenceUnallocatedPurchaseModel>> {
    return this.apiService.get(`/intelligence/purchasing/purchase_lines?page=${ page }${ this.filtersQuery({ ...filters, ...this.sortQuery(sort), limit: limit ? '' + limit : '', view }) }`);
  }

  getSummary () : Promise<IntelligenceSummaryModel> {
    return this.withHistoryBoundary(this.apiService.get(`/intelligence/summary?${ this.rangeQuery() }`));
  }

  getVehicles ( filters : IntelligenceVehicleFiltersModel = { body_type: '', drive_type: '', fuel_type: '', make: '' } ) : Promise<IntelligenceVehiclesModel> {
    this.recordFilters('vehicles', filters);

    return this.withHistoryBoundary(this.apiService.get(`/intelligence/vehicles?${ this.rangeQuery() }${ this.filtersQuery(filters) }`));
  }

  // True when the chosen range reaches back past the figures, so the tabs are showing
  // a shorter period than the picker says.
  historyLimited () : boolean {
    return !!this.earliestDate && this.range.start_date < this.earliestDate;
  }

  presetRange ( preset : string ) : IntelligenceRangeModel {
    const today = this.companyToday();
    const end = this.toIso(today);

    switch (preset) {
      case 'TODAY':
        return { end_date: end, start_date: end };
      case 'THIS_WEEK':
        return { end_date: end, start_date: this.toIso(this.startOfWeek(today)) };
      case 'LAST_WEEK':
        return {
          end_date  : this.toIso(this.addDays(this.startOfWeek(today), -1)),
          start_date: this.toIso(this.addDays(this.startOfWeek(today), -7))
        };
      case 'THIS_MONTH':
        return { end_date: end, start_date: this.toIso(new Date(today.getFullYear(), today.getMonth(), 1)) };
      case 'LAST_MONTH':
        return {
          end_date  : this.toIso(new Date(today.getFullYear(), today.getMonth(), 0)),
          start_date: this.toIso(new Date(today.getFullYear(), today.getMonth() - 1, 1))
        };
      case 'YTD':
        return { end_date: end, start_date: this.toIso(new Date(today.getFullYear(), 0, 1)) };
      default:
        return { end_date: end, start_date: this.toIso(this.addDays(today, -29)) };
    }
  }

  restoreRange () : void {
    if ( !this.previousRange ) {
      return;
    }

    this.setRange(this.previousRange);
  }

  selectDay ( date : string ) : void {
    if ( !date ) {
      return;
    }

    const previous = this.previousRange || this.range;

    this.setRange({ end_date: date, start_date: date });

    this.previousRange = previous;
  }

  setRange ( range : IntelligenceRangeModel ) : void {
    this.previousRange = null;
    this.range = range;

    this.rangeChanged.next(range);
  }

  private addDays ( date : Date, days : number ) : Date {
    const copy = new Date(date.getTime());

    copy.setDate(copy.getDate() + days);

    return copy;
  }

  private companyToday () : Date {
    const now = moment().utcOffset(this.companyService.getUtcOffset());

    return new Date(now.year(), now.month(), now.date());
  }

  // Sort rides the same query builder as the filters; a null sort sends nothing and
  // the backend keeps the list's default order.
  private sortQuery ( sort : TableSortModel ) : { direction : string; sort : string } {
    return {
      direction: sort ? sort.direction : '',
      sort     : sort ? sort.field : ''
    };
  }

  private filtersQuery ( filters : object ) : string {
    const params = filters as { [ key : string ] : string };

    return Object.keys(params)
      .filter(key => !!params[ key ])
      .sort()
      .map(key => `&${ key }=${ encodeURIComponent(params[ key ]) }`)
      .join('');
  }

  private rangeQuery () : string {
    return `start_date=${ this.range.start_date }&end_date=${ this.range.end_date }`;
  }

  // Every tab reports where the figures start, so whichever one loads first tells the
  // dashboard. The response is passed straight through untouched.
  private withHistoryBoundary ( request : Promise<any> ) : Promise<any> {
    return request.then(( response : any ) => {
      if ( response && response.earliest_date ) {
        this.earliestDate = response.earliest_date;
      }

      return response;
    });
  }

  // Notifies only on a real change, so anything showing derived state (the AI summary)
  // is retired when the view it described is narrowed, but re-reading the same filters
  // does not disturb it.
  private recordFilters ( dataset : string, filters : any ) : void {
    const next = { ...filters };
    const previous = this.activeFilters[ dataset ];

    this.activeFilters[ dataset ] = next;

    if ( previous && JSON.stringify(previous) !== JSON.stringify(next) ) {
      this.filtersChanged.next(dataset);
    }
  }

  private startOfWeek ( date : Date ) : Date {
    const copy = new Date(date.getTime());

    copy.setDate(copy.getDate() - ( ( copy.getDay() + 6 ) % 7 ));

    return copy;
  }

  private toIso ( date : Date ) : string {
    const month = `0${ date.getMonth() + 1 }`.slice(-2);
    const day = `0${ date.getDate() }`.slice(-2);

    return `${ date.getFullYear() }-${ month }-${ day }`;
  }
}
